import type { ParsedParam } from "@/lib/mcp/schema";
import type {
  PromptExecution,
  PromptState,
  ToolExecution,
  ToolState,
} from "@/stores/inspectorStore";
import { useCallback, useEffect, useRef } from "react";
import { parametersMatch } from "../utils/execution";
import { buildDefaultValues } from "../utils/formatting";
import { validateParameterValue } from "../utils/validation";

export interface UseParameterManagementOptions<T extends "tool" | "prompt"> {
  type: T;
  selectedName: string | null;
  parsedParams: ParsedParam[];
  getState: (name: string) => T extends "tool" ? ToolState : PromptState;
  updateParameters: (name: string, params: Record<string, string>) => void;
  updateErrors: (name: string, errors: Record<string, string>) => void;
  setResponse: (name: string, response: unknown) => void;
  invalidateConfigureSection: () => void;
}

export function useParameterManagement<T extends "tool" | "prompt">(
  options: UseParameterManagementOptions<T>,
) {
  const {
    type,
    selectedName,
    parsedParams,
    getState,
    updateParameters,
    updateErrors,
    setResponse,
    invalidateConfigureSection,
  } = options;

  // Build default values when parameters change
  const defaultValues = buildDefaultValues(parsedParams);

  // Get current state
  const currentState = selectedName ? getState(selectedName) : null;
  const paramValues = currentState
    ? type === "tool"
      ? (currentState as ToolState).parameters
      : (currentState as PromptState).arguments
    : {};
  const paramErrors = currentState
    ? type === "tool"
      ? (currentState as ToolState).paramErrors
      : (currentState as PromptState).argumentErrors
    : {};

  // Initialize parameters to defaults when switching selection
  const lastSelectedRef = useRef<string | null>(null);

  useEffect(() => {
    if (lastSelectedRef.current !== selectedName && selectedName) {
      lastSelectedRef.current = selectedName;
      const currentParams =
        type === "tool"
          ? (getState(selectedName) as ToolState).parameters
          : (getState(selectedName) as PromptState).arguments;

      // Only set defaults if no parameters are set yet
      if (Object.keys(currentParams).length === 0) {
        updateParameters(selectedName, defaultValues);
      }
    }
  }, [selectedName, defaultValues, getState, updateParameters, type]);

  // Validate and set parameter value
  const validateAndSetParameter = useCallback(
    (name: string, value: string, param: ParsedParam) => {
      if (!selectedName) {
        return;
      }

      const validation = validateParameterValue(value, param);

      // Update parameter value
      const newParams = { ...paramValues, [name]: value };
      updateParameters(selectedName, newParams);

      // Check if we need to invalidate based on parameter changes
      if (currentState?.lastResponse || currentState?.lastError) {
        const executionHistory = currentState.executionHistory;
        if (executionHistory && executionHistory.length > 0) {
          const lastExecution = executionHistory[0]; // Most recent is first
          const lastExecutionParams =
            type === "tool"
              ? (lastExecution as ToolExecution).parameters
              : (lastExecution as PromptExecution).arguments;

          const paramsMatch = parametersMatch(newParams, lastExecutionParams);

          if (paramsMatch) {
            // Parameters match last execution, mark as completed
            setResponse(selectedName, currentState.lastResponse);
          } else {
            // Parameters differ, invalidate
            invalidateConfigureSection();
          }
        }
      }

      // Update validation errors
      const newErrors = {
        ...paramErrors,
        [name]: validation.isValid ? undefined : validation.error,
      };
      // Filter out undefined values for the updateErrors call
      const filteredErrors = Object.fromEntries(
        Object.entries(newErrors).filter(([, value]) => value !== undefined),
      ) as Record<string, string>;
      updateErrors(selectedName, filteredErrors);
    },
    [
      selectedName,
      paramValues,
      paramErrors,
      currentState,
      updateParameters,
      updateErrors,
      setResponse,
      invalidateConfigureSection,
      type,
    ],
  );

  return {
    paramValues,
    paramErrors,
    validateAndSetParameter,
    defaultValues,
    currentState,
  };
}
